import { AdminPageHeader } from '@/components/patterns/admin-page'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { useOrganizationContext } from '@/hooks/useOrganizationContext'
import { usePathHelper } from '@/hooks/usePathHelper'
import { OrganizationType } from '@/lib/enums/OrganizationType'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Network, PackageOpen, Upload, Users } from 'lucide-react'

export const Route = createFileRoute('/_authenticated/$orgId/imports/')({
  component: ImportsPage,
})

export function ImportsPage() {
  const { organization } = useOrganizationContext()
  const path = usePathHelper()
  const isGlobalOrg = organization?.type === OrganizationType.Global.Value

  if (isGlobalOrg) {
    return (
      <div className="max-w-4xl space-y-4">
        <AdminPageHeader
          icon={Upload}
          title="Imports"
          description="Import customer and other business data into the shop you are impersonating."
        />

        <OrganizationContextNotice title="Choose an organization first">
          Imports only run against the florist account you are currently impersonating. Choose the
          correct shop before you start an import.
        </OrganizationContextNotice>
      </div>
    )
  }

  return (
    <div className="max-w-6xl space-y-4">
      <AdminPageHeader
        icon={Upload}
        title="Imports"
        description={`Import customer and other business data into ${organization?.name ?? 'this shop'}.`}
      />

      <div className="grid items-stretch gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Card className="flex h-full flex-col">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Users className="size-5 text-interaction" />
              Customer Import
            </CardTitle>
            <CardDescription>
              Upload a spreadsheet of customer rows and preview the import before you create
              anything.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1">
            <Typography variant="supporting" className="block">
              Choose Flower Shop Network or The Floral POS, then upload the matching spreadsheet or
              CSV customer export.
            </Typography>
          </CardContent>
          <CardFooter className="mt-auto">
            <Button asChild>
              <Link {...path.customerImport.index().link}>
                <Users className="size-4" />
                Import Customers
              </Link>
            </Button>
          </CardFooter>
        </Card>

        <Card className="flex h-full flex-col">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <PackageOpen className="size-5 text-interaction" />
              Product Import
            </CardTitle>
            <CardDescription>
              Upload the shop&apos;s product export, preview what will change, and keep the original
              FSN product IDs for matching later.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1">
            <Typography variant="supporting" className="block">
              Use Products on active pages for normal FSN migrations, or All active products when
              the shop wants everything. Products that are not in the file stay as they are.
            </Typography>
          </CardContent>
          <CardFooter className="mt-auto">
            <Button asChild>
              <Link {...path.productImport.index().link}>
                <PackageOpen className="size-4" />
                Import Products
              </Link>
            </Button>
          </CardFooter>
        </Card>

        <Card className="flex h-full flex-col">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Network className="size-5 text-interaction" />
              Flower Shop Network Import
            </CardTitle>
            <CardDescription>
              Find the FSN client record before choosing products, add-ons, reviews, and shop data.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1">
            <Typography variant="supporting" className="block">
              Search by client ID, phone number, or shop name. This first step only confirms the FSN
              lookup.
            </Typography>
          </CardContent>
          <CardFooter className="mt-auto">
            <Button asChild>
              <Link {...path.fsnImport.index().link}>
                <Network className="size-4" />
                Open FSN Import
              </Link>
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  )
}
